import { DELETED_SEGMENT_LABEL, UNAVAILABLE_SEGMENT_LABEL } from '~/constants/segment';
import { segmentsClient } from '~/services';
import type { ClientContext } from '~/services/contextClient';
import type { Segment } from '~/services/segments/segmentsClient.types';
import { chunkArray } from '~/utils/array';

const IDS_PER_REQUEST = 10;

export interface SegmentsByIds {
    byId: Map<number, Segment>;
    failedIds: Set<number>;
}

// Names for ids the caller already holds, kept apart from the lookups that failed so a segment
// the API no longer returns can be told from one it could not be asked about.
export const fetchSegmentsByIds = async (context: ClientContext, ids: number[]): Promise<SegmentsByIds> => {
    const byId = new Map<number, Segment>();
    const failedIds = new Set<number>();

    await Promise.all(
        chunkArray([...new Set(ids)], IDS_PER_REQUEST).map(async (batch) => {
            try {
                const response = await segmentsClient.getSegments(context, {
                    per_page: batch.length,
                    filter: { ids: batch },
                });
                response.data.data.forEach((segment) => byId.set(Number(segment.id), segment));
            } catch {
                batch.forEach((id) => failedIds.add(id));
            }
        })
    );

    return { byId, failedIds };
};

export const segmentName = ({ byId, failedIds }: SegmentsByIds, id: number): string =>
    byId.get(id)?.name ?? (failedIds.has(id) ? UNAVAILABLE_SEGMENT_LABEL : DELETED_SEGMENT_LABEL);
